// Racetrack data for koreasnowwatch.com. Same shape as src/data/racetracks.ts, plus:
//   - `zh` on every localised string
//   - `timelapseGifUrl` and `routes` (both were hard-coded in the old site's RacetrackDetailPage.tsx)
// Separate from ski resorts - one-off additions
import { RACETRACK_GUIDE, type RacetrackGuide } from './guides/racetrackGuide';

export interface POI {
  id: string;
  name: {
    ko: string;
    en: string;
    ja: string;
    zh: string;
  };
  type: 'track' | 'pit' | 'grandstand' | 'hotel' | 'entrance' | 'camera' | 'city' | 'parking';
  coordinates: {
    latitude: number;
    longitude: number;
  };
}

/** Drawn on the location map. Same fields as MapPolyline in the map component. */
export interface RacetrackRoute {
  id: string;
  name: {
    ko: string;
    en: string;
    ja: string;
    zh: string;
  };
  color: string;
  weight?: number;
  opacity?: number;
  dashArray?: string;
  coordinates: [number, number][]; // [lat, lng][]
}

export interface Racetrack {
  id: string;
  slug: string;
  name: {
    ko: string;
    en: string;
    ja: string;
    zh: string;
  };
  location: {
    region: string;
    coordinates: {
      latitude: number;
      longitude: number;
    };
  };
  contact: {
    homepage: string;
    maps?: {
      google?: string;
      naver?: string;
      kakao?: string;
    };
  };
  specifications: {
    mainTrackLength: number; // in km
    turns: number;
    additionalTracks?: { name: string; length: number }[];
  };
  streams: {
    id: string;
    name: {
      ko: string;
      en: string;
      ja: string;
      zh: string;
    };
    type: 'HLS' | 'External' | 'IFrame';
    url: string;
    isLive: boolean;
    /** HTTP-only origin: browsers on https pages block it unless proxied (see SOURCES.md). */
    httpOnly?: boolean;
  }[];
  description: {
    ko: string;
    en: string;
    ja: string;
    zh: string;
  };
  pointsOfInterest?: POI[];
  /** One-hour timelapse GIF for the card/preview (was hard-coded in RacetrackDetailPage). */
  timelapseGifUrl?: string;
  /** Map polylines (was hard-coded in RacetrackDetailPage). */
  routes?: RacetrackRoute[];
  /** Editorial guide (./guides/racetrackGuide.ts). */
  guide?: Omit<RacetrackGuide, 'slug' | 'sourceUrls'>;
}

export const racetracks: Racetrack[] = [
  {
    id: 'inje-speedium',
    slug: 'inje-speedium',
    name: {
      ko: '인제 스피디움',
      en: 'Inje Speedium',
      ja: 'インジェ・スピーディウム',
      zh: '麟蹄赛车场',
    },
    location: {
      region: 'Inje, Gangwon',
      coordinates: {
        latitude: 38.001389,
        longitude: 128.291944,
      },
    },
    contact: {
      homepage: 'https://www.speedium.co.kr/',
      maps: {
        google: 'https://maps.app.goo.gl/UqHjgNcMvSVS1JZ6A',
        naver: 'https://naver.me/5XJIVGpU',
        kakao: 'https://place.map.kakao.com/26537929',
      },
    },
    specifications: {
      // Wikipedia (Inje Speedium): full circuit 3.908 km / 20 turns,
      // North Circuit 2.577 km / 12 turns, South Circuit 1.375 km / 8 turns.
      // The old site called the two short layouts "East" and "West".
      mainTrackLength: 3.908,
      turns: 20,
      additionalTracks: [
        { name: 'North Circuit', length: 2.577 },
        { name: 'South Circuit', length: 1.375 },
      ],
    },
    streams: [
      {
        id: 'inje-main',
        name: {
          ko: '메인 트랙',
          en: 'Main Track',
          ja: 'メイントラック',
          zh: '主赛道',
        },
        type: 'HLS',
        url: 'http://14.54.22.28:1935/live/inje.stream/chunklist.m3u8',
        isLive: true,
        httpOnly: true,
      },
    ],
    description: {
      ko: '인제 스피디움은 서울에서 동쪽으로 약 120km 떨어진 강원도 인제군에 있는 모터 레이싱 서킷입니다. 2013년 5월에 문을 열었으며, 호텔과 콘도미니엄을 갖춘 인제 오토테마파크의 일부입니다.',
      en: 'Inje Speedium is a motor racing circuit in Inje County, Gangwon, about 120 km east of Seoul. It opened in May 2013 and is part of the Inje Auto Theme Park complex, which includes a hotel and condominiums.',
      ja: 'インジェ・スピーディウムは、ソウルの東約120kmの江原道麟蹄郡にあるモーターレーシングサーキットです。2013年5月に開業し、ホテルとコンドミニアムを備えた麟蹄オートテーマパークの一部です。',
      zh: '麟蹄赛车场位于江原道麟蹄郡，距首尔以东约120公里，于2013年5月开业，是包含酒店和公寓式度假村的麟蹄汽车主题公园的一部分。',
    },
    pointsOfInterest: [
      {
        id: 'seoul',
        name: { ko: '서울 (~120km)', en: 'Seoul (~120km)', ja: 'ソウル (~120km)', zh: '首尔（约120公里）' },
        type: 'city',
        coordinates: { latitude: 37.5665, longitude: 126.9780 },
      },
    ],
    timelapseGifUrl: 'https://cdn.koreasnow.cam/images/latest_inje_speedium_cam0.gif',
    routes: [
      {
        // Highway route from Seoul to Inje (via Gyeongchun Expressway & Route 46)
        id: 'route-seoul',
        name: {
          ko: '서울 → 인제 (~120km)',
          en: 'Seoul → Inje (~120km)',
          ja: 'ソウル → 麟蹄 (~120km)',
          zh: '首尔 → 麟蹄（约120公里）',
        },
        color: '#3b82f6',
        weight: 3,
        opacity: 0.7,
        dashArray: '10, 10',
        coordinates: [
          [37.5665, 126.9780], // Seoul
          [37.5200, 127.0500], // Heading east on Olympic Expressway
          [37.5800, 127.1300], // Guri Junction
          [37.6500, 127.2200], // Gyeongchun Expressway north
          [37.7500, 127.5000], // Near Chuncheon
          [37.8800, 127.7300], // Chuncheon
          [37.9200, 127.9000], // Route 46 towards Inje
          [37.9500, 128.0500], // Hongcheon area
          [37.9800, 128.1800], // Approaching Inje
          [38.0014, 128.2920], // Inje Speedium
        ],
      },
    ],
    guide: RACETRACK_GUIDE,
  },
];

export function getRacetrack(slugOrId: string): Racetrack | undefined {
  return racetracks.find(r => r.slug === slugOrId || r.id === slugOrId);
}
